import {
    Breadcrumb,
    BreadcrumbItem,
    BreadcrumbLink,
    BreadcrumbList,
    BreadcrumbSeparator,
} from "@patient-watch/ui/breadcrumb";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "@patient-watch/ui/dropdown-menu";
import { Separator } from "@patient-watch/ui/separator";
import { cn } from "@/lib/utils";
import type { ToT } from "@/route-types";
import { useNav } from "@/utils/nav";
import { ChevronDownIcon } from "@radix-ui/react-icons";
import { useNavigate } from "@tanstack/react-router";
import React from "react";

export interface BreadcrumbItemT {
    label: React.ReactNode;
    route: ToT;
    status?: "draft" | "published" | "licensed" | "unsaved";
    options?: Array<{
        label: string;
        route: ToT;
    }>;
}

const BREADCRUMB_STATUS_LABEL: Record<NonNullable<BreadcrumbItemT["status"]>, string> = {
    draft: "Draft",
    published: "Published",
    licensed: "Licensed",
    unsaved: "Not saved",
};

const BREADCRUMB_STATUS_TONE: Record<NonNullable<BreadcrumbItemT["status"]>, string> = {
    draft: "border-amber-200 bg-amber-50 text-amber-800",
    published: "border-emerald-200 bg-emerald-50 text-emerald-800",
    licensed: "border-slate-200 bg-slate-50 text-slate-600",
    unsaved: "border-slate-200 bg-slate-50 text-slate-600",
};

function BreadcrumbStatus({ status }: { status: NonNullable<BreadcrumbItemT["status"]> }) {
    return (
        <span
            className={cn(
                "mt-0.5 shrink-0 rounded border px-1.5 py-0 text-2xs font-medium leading-4 normal-case",
                BREADCRUMB_STATUS_TONE[status]
            )}
        >
            {BREADCRUMB_STATUS_LABEL[status]}
        </span>
    );
}

interface Props {
    backOnly?: boolean;
}

const NavigationBreadcrumb = ({ backOnly }: Props) => {
    const navigate = useNavigate();
    const nav = useNav();

    const routes = (nav.breadcrumbRoutes ?? []).filter((item, index) => {
        return !(index === 0 && item.route?.to === "/");
    });
    const routeCount = routes.length;
    const lastItem = routeCount > 0 ? routes[routeCount - 1] : undefined;
    const lastSegmentHasDropdown = Boolean(lastItem?.options?.length);

    const penultimateBreadcrumb = () => {
        try {
            const breadcrumbs = routes;

            if (breadcrumbs.length > 1) {
                return breadcrumbs[breadcrumbs.length - 2];
            }
            if (breadcrumbs.length === 1) {
                return breadcrumbs[0];
            }
            return null;
        } catch (e) {
            console.error(e);
            return null;
        }
    };

    if (routeCount === 0) return null;

    return (
        <>
            <Separator className="dark:bg-slate-200 h-8" orientation="vertical" />

            <Breadcrumb>
                <BreadcrumbList className="text-slate-800 dark:text-slate-200 capitalize">
                    {backOnly ? (
                        <BreadcrumbItem>
                            <BreadcrumbLink
                                className="text-lg"
                                onClick={() => navigate(penultimateBreadcrumb()?.route)}
                            >
                                {penultimateBreadcrumb()?.label}
                            </BreadcrumbLink>
                        </BreadcrumbItem>
                    ) : (
                        routes.map((item, index) => {
                            const isCurrentPage = index === routeCount - 1;
                            const hasOptions = Boolean(item?.options?.length);
                            const hidePlainCurrentOnMobile = isCurrentPage && !hasOptions;
                            return (
                                <React.Fragment key={`breadcrumb_item_${index}`}>
                                    {hasOptions ? (
                                        <BreadcrumbItem className="max-md:px-1 max-md:py-0">
                                            <DropdownMenu>
                                                <DropdownMenuTrigger
                                                    className={cn(
                                                        "flex items-center gap-0.5 rounded-md outline-none",
                                                        "max-md:min-h-0 max-md:py-0.5 max-md:pl-0.5 max-md:pr-1",
                                                        "text-left text-base md:text-inherit"
                                                    )}
                                                >
                                                    {item?.label}
                                                    <ChevronDownIcon className="size-4 max-md:size-3.5 shrink-0" />
                                                </DropdownMenuTrigger>
                                                <DropdownMenuContent align="start" className="p-0.5 max-md:p-0.5">
                                                    {item.options?.map((option) => (
                                                        <React.Fragment key={`breadcrumb_option_${option.label}`}>
                                                            <DropdownMenuItem
                                                                className="max-md:px-2 max-md:py-1 max-md:text-sm"
                                                                onClick={() => navigate(option.route)}
                                                            >
                                                                {option.label}
                                                            </DropdownMenuItem>
                                                        </React.Fragment>
                                                    ))}
                                                </DropdownMenuContent>
                                            </DropdownMenu>
                                        </BreadcrumbItem>
                                    ) : (
                                        <BreadcrumbItem
                                            className={cn(
                                                hidePlainCurrentOnMobile ? "max-md:hidden md:inline-flex" : undefined,
                                                item.status ? "items-start" : undefined
                                            )}
                                        >
                                            <BreadcrumbLink onClick={() => navigate(item?.route)}>
                                                {item?.label}
                                            </BreadcrumbLink>
                                            {item.status ? <BreadcrumbStatus status={item.status} /> : null}
                                        </BreadcrumbItem>
                                    )}
                                    {index !== routeCount - 1 && (
                                        <BreadcrumbSeparator
                                            className={
                                                index === routeCount - 2 && !lastSegmentHasDropdown
                                                    ? "max-md:hidden md:list-item"
                                                    : undefined
                                            }
                                        />
                                    )}
                                </React.Fragment>
                            );
                        })
                    )}
                </BreadcrumbList>
            </Breadcrumb>
        </>
    );
};

export default NavigationBreadcrumb;
